import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useData, save } from "../hooks/useData";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { PageHeader, DataTable, Button, Modal, Field, Input, Textarea, Select, StatusBadge } from "../components/shared";
import { Plus, X, Printer, Mail, MessageCircle, PackageCheck, Trash2 } from "lucide-react";
import { money, num, fmtDate } from "../lib/api";

const ACC_CATEGORIES = ["Elastic", "Pocketing", "Size Label", "Patches", "Dori", "Tags", "Box/Carton", "Polly", "Others", "Rivet/Button", "Thread"];

const lineDue = (i) => Math.max(0, i.balance_qty ?? ((Number(i.qty) || 0) - (Number(i.received_qty) || 0))) * (Number(i.rate) || 0);

function DueSummary({ rows }) {
  const today = new Date().toISOString().slice(0, 10);
  const open = (rows || []).filter((r) => r.status !== "Cancelled").map((r) => ({ r, due: (r.items || []).reduce((s, i) => s + lineDue(i), 0) })).filter((x) => x.due > 0);
  const total = open.reduce((s, x) => s + x.due, 0);
  const late = open.filter((x) => x.r.expected_receiving_date && String(x.r.expected_receiving_date).slice(0, 10) < today);
  const lateVal = late.reduce((s, x) => s + x.due, 0);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
      <div className="bg-white border border-slate-200 rounded-lg p-4"><p className="text-xs uppercase text-slate-500">Total Value Due to Receive</p><p className="text-2xl font-bold text-blue-700 tabular" data-testid="po-due-total">{money(total)}</p></div>
      <div className="bg-white border border-slate-200 rounded-lg p-4"><p className="text-xs uppercase text-slate-500">Open Purchase Orders</p><p className="text-2xl font-bold text-slate-800 tabular" data-testid="po-due-count">{open.length}</p></div>
      <div className="bg-white border border-slate-200 rounded-lg p-4"><p className="text-xs uppercase text-slate-500">Overdue Value ({late.length} POs)</p><p className="text-2xl font-bold text-red-600 tabular" data-testid="po-due-overdue">{money(lateVal)}</p></div>
    </div>
  );
}

export default function PurchaseOrders() {
  const { data, loading, reload } = useData("/fabric-orders");
  const { data: suppliers, reload: reloadSup } = useData("/suppliers");
  const { data: accessories, reload: reloadAcc } = useData("/accessories");
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [receiveLine, setReceiveLine] = useState(null);
  const [recForm, setRecForm] = useState(null);
  const [newAcc, setNewAcc] = useState(null);
  const [f, setF] = useState({ from: "", to: "", item: "", vendor: "" });

  const blank = { supplier_id: "", supplier_name: "", supplier_email: "", supplier_phone: "", party_contract: "", order_date: new Date().toISOString().slice(0, 10), expected_receiving_date: "", remarks: "", items: [] };

  const openForm = (r) => {
    if (r) setForm({ ...r, items: (r.items || []).map((i) => ({ ...i })) });
    else setForm({ ...blank });
    setOpen(true);
  };

  const pickSupplier = (id) => {
    const s = (suppliers || []).find((x) => x.id === id);
    setForm((ff) => ({ ...ff, supplier_id: id, supplier_name: s?.name || "", supplier_email: s?.email || ff.supplier_email, supplier_phone: s?.phone || ff.supplier_phone }));
  };

  const addLine = (category) => setForm((ff) => ({ ...ff, items: [...ff.items, category === "Fabric"
    ? { category: "Fabric", fabric_type: "", colour: "", unit: "MTR", description: "", qty: "", rate: "" }
    : { category: "Accessory", accessory_id: "", item_name: "", unit: "Pcs", qty: "", rate: "" }] }));
  const updLine = (i, k, v) => setForm((ff) => ({ ...ff, items: ff.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const rmLine = (i) => setForm((ff) => ({ ...ff, items: ff.items.filter((_, idx) => idx !== i) }));
  const pickAcc = (i, id) => { const a = (accessories || []).find((x) => x.id === id); updLine(i, "accessory_id", id); if (a) { updLine(i, "item_name", a.name); updLine(i, "unit", a.unit); } };
  const typeAcc = (i, text) => {
    const a = (accessories || []).find((x) => x.name.toLowerCase() === text.trim().toLowerCase());
    updLine(i, "item_name", text); updLine(i, "accessory_id", a ? a.id : "");
    if (a) updLine(i, "unit", a.unit);
  };

  const total = (form?.items || []).reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.items.length) { toast.error("Add at least one item"); return; }
    const body = { ...form, items: form.items.map((it) => ({ ...it, qty: parseFloat(it.qty) || 0, rate: parseFloat(it.rate) || 0 })) };
    await save(form.id ? "put" : "post", form.id ? `/fabric-orders/${form.id}` : "/fabric-orders", body);
    toast.success("Purchase order saved"); setOpen(false); reload();
  };

  const openDetail = async (r) => { const { data: d } = await api.get(`/fabric-orders/${r.id}`); setDetail(d); };

  const delPO = async (r) => {
    if (!isAdmin) return;
    if (!window.confirm(`This will permanently delete Fabric Order ${r.code}. Do you want to continue?`)) return;
    const reason = window.prompt("Reason for deletion (recorded in the audit log):", "");
    if (reason === null) return;
    try { await api.delete(`/fabric-orders/${r.id}`, { params: { reason } }); toast.success("Fabric order deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const openReceive = (line) => {
    setReceiveLine(line);
    setRecForm({ line_id: line.line_id, received_qty: line.balance_qty, lot_number: "", bill_number: "", bill_date: "", rolls: "", transporter: "", docket_number: "", receiving_date: new Date().toISOString().slice(0, 10), remarks: "" });
  };
  const submitReceive = async (e) => {
    e.preventDefault();
    try {
      const d = await save("post", `/fabric-orders/${detail.id}/receive`, { ...recForm, received_qty: parseFloat(recForm.received_qty) || 0 });
      toast.success(receiveLine.category === "Fabric" ? "Received — Lot created" : "Received — accessory stock updated");
      setReceiveLine(null); setRecForm(null); setDetail(d); reload();
    } catch {}
  };

  const sendEmail = async (r) => {
    const to = r.supplier_email || window.prompt("Supplier email:");
    if (!to) return;
    try { await api.post(`/fabric-orders/${r.id}/email`, { to }); toast.success(`PO emailed to ${to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Email failed"); }
  };
  const sendWhatsApp = (r) => {
    const lines = (r.items || []).map((i) => `• ${i.fabric_type || i.item_name} ${i.colour || ""} — ${i.qty} ${i.unit} @ ${i.rate}`).join("\n");
    const msg = `*ASV Apparels — Purchase Order ${r.code}*\nDate: ${fmtDate(r.order_date)}\nSupplier: ${r.supplier_name}\n\n${lines}\n\nTotal: ₹${num(r.total_amount)}\nExpected: ${fmtDate(r.expected_receiving_date)}`;
    const phone = (r.supplier_phone || "").replace(/[^0-9]/g, "");
    if (!phone) { toast.error("Supplier has no phone number for WhatsApp"); return; }
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const createAcc = async (e) => {
    e.preventDefault();
    const a = await save("post", "/accessories", newAcc);
    toast.success("Accessory item created"); await reloadAcc();
    if (newAcc.lineIdx != null) pickAcc(newAcc.lineIdx, a.id);
    setNewAcc(null);
  };

  const vendors = useMemo(() => [...new Set((data || []).map((o) => o.supplier_name).filter(Boolean))], [data]);
  const filtered = useMemo(() => (data || []).filter((o) => {
    if (f.from && (o.order_date || "") < f.from) return false;
    if (f.to && (o.order_date || "") > f.to) return false;
    if (f.vendor && o.supplier_name !== f.vendor) return false;
    if (f.item) { const q = f.item.toLowerCase(); if (!(o.items || []).some((i) => `${i.fabric_type || ""} ${i.item_name || ""} ${i.colour || ""} ${i.category}`.toLowerCase().includes(q))) return false; }
    return true;
  }), [data, f]);

  const columns = [
    { key: "code", header: "PO Number", mono: true, render: (r) => <span className="font-semibold text-blue-800">{r.code}</span> },
    { key: "order_date", header: "PO Date", render: (r) => fmtDate(r.order_date) },
    { key: "supplier_name", header: "Supplier Name" },
    { key: "items", header: "Item", sortable: false, accessor: (r) => (r.items || []).map((i) => [i.fabric_type || i.item_name, i.colour].filter(Boolean).join(" ")).join(", "),
      render: (r) => <div className="text-xs space-y-0.5">{(r.items || []).map((i, k) => <div key={k}>{[i.fabric_type || i.item_name, i.colour].filter(Boolean).join(" · ") || "—"}</div>)}</div> },
    { key: "qty", header: "Quantity", align: "right", sortable: false, accessor: (r) => (r.items || []).reduce((s, i) => s + (Number(i.qty) || 0), 0),
      render: (r) => <div className="text-xs space-y-0.5 tabular">{(r.items || []).map((i, k) => <div key={k}>{num(i.qty)} {i.unit}</div>)}</div> },
    { key: "rate", header: "Rate", align: "right", sortable: false, accessor: (r) => (r.items || []).map((i) => i.rate).join(", "),
      render: (r) => <div className="text-xs space-y-0.5 tabular">{(r.items || []).map((i, k) => <div key={k}>{money(i.rate)}</div>)}</div> },
    { key: "total_amount", header: "Total Amount", align: "right", render: (r) => <span className="font-semibold">{money(r.total_amount)}</span> },
    { key: "expected_receiving_date", header: "Expected Delivery", render: (r) => {
      if (!r.expected_receiving_date) return "—";
      const late = !["Fully Received", "Received", "Closed", "Completed", "Cancelled"].includes(r.status) && String(r.expected_receiving_date).slice(0, 10) < new Date().toISOString().slice(0, 10);
      return late
        ? <span className="text-red-600 font-semibold" title="Late — not fully received" data-testid={`po-overdue-${r.id}`}>{fmtDate(r.expected_receiving_date)}<span className="block text-[10px] font-medium uppercase">Overdue</span></span>
        : fmtDate(r.expected_receiving_date);
    } },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "act", header: "Actions", sortable: false, render: (r) => (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Button size="sm" variant="secondary" onClick={() => openDetail(r)} data-testid={`po-receive-${r.id}`}><PackageCheck size={13} /> Receive</Button>
        <button title="Print" onClick={() => navigate(`/purchase-orders/${r.id}/print`)} className="p-1.5 text-slate-500 hover:text-blue-700"><Printer size={15} /></button>
        <button title="Email" onClick={() => sendEmail(r)} className="p-1.5 text-slate-500 hover:text-emerald-700" data-testid={`po-email-${r.id}`}><Mail size={15} /></button>
        <button title="WhatsApp" onClick={() => sendWhatsApp(r)} className="p-1.5 text-slate-500 hover:text-green-600" data-testid={`po-wa-${r.id}`}><MessageCircle size={15} /></button>
        <button title="Edit" onClick={() => openForm(r)} className="p-1.5 text-slate-500 hover:text-slate-800">Edit</button>
        {isAdmin && <button title="Delete" onClick={() => delPO(r)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`po-delete-${r.id}`}><Trash2 size={15} /></button>}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Purchase Orders" subtitle="Order fabric & accessories, receive against lines, print / email / WhatsApp">
        <Button onClick={() => openForm(null)} data-testid="add-po-btn"><Plus size={15} /> New Purchase Order</Button>
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4 flex flex-wrap items-end gap-3">
        <div><label className="block text-xs text-slate-500 mb-1">From Date</label><Input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className="w-40" data-testid="po-filter-from" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">To Date</label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className="w-40" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Item</label><Input placeholder="fabric / accessory / colour" value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} className="w-52" data-testid="po-filter-item" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Vendor</label><Select value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} placeholder="All vendors" options={vendors} className="w-52" data-testid="po-filter-vendor" /></div>
        {(f.from || f.to || f.item || f.vendor) && <Button variant="ghost" size="sm" onClick={() => setF({ from: "", to: "", item: "", vendor: "" })}>Clear</Button>}
      </div>

      {!loading && <DueSummary rows={filtered} />}

      {!loading && <DataTable testid="po-table" columns={columns} rows={filtered} exportName="purchase-orders.csv" onRowClick={openDetail} />}

      {/* Create / Edit */}
      <Modal open={open} onClose={() => setOpen(false)} title={form?.id ? "Edit Purchase Order" : "New Purchase Order"} wide>
        {form && (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <Field label="Supplier" required>
                <Select value={form.supplier_id} onChange={(e) => pickSupplier(e.target.value)} required placeholder="Select supplier"
                  options={(suppliers || []).map((s) => ({ value: s.id, label: s.name }))} data-testid="po-supplier" />
              </Field>
              <Field label="Supplier Email"><Input type="email" value={form.supplier_email} onChange={(e) => setForm({ ...form, supplier_email: e.target.value })} placeholder="for email PO" /></Field>
              <Field label="Supplier Phone (WhatsApp)"><Input value={form.supplier_phone} onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })} placeholder="91XXXXXXXXXX" /></Field>
              <Field label="Order Date"><Input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} /></Field>
              <Field label="Expected Receiving"><Input type="date" value={form.expected_receiving_date} onChange={(e) => setForm({ ...form, expected_receiving_date: e.target.value })} /></Field>
              <Field label="Party Contract No"><Input value={form.party_contract} onChange={(e) => setForm({ ...form, party_contract: e.target.value })} /></Field>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-slate-700">Order Items</p>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="secondary" onClick={() => addLine("Fabric")} data-testid="po-add-fabric">+ Fabric</Button>
                  <Button type="button" size="sm" variant="secondary" onClick={() => addLine("Accessory")} data-testid="po-add-accessory">+ Accessory</Button>
                </div>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded-md p-2">
                    <span className="col-span-12 text-xs font-semibold text-slate-500">{it.category} line</span>
                    {it.category === "Fabric" ? (
                      <>
                        <Input className="col-span-3" placeholder="Fabric type" value={it.fabric_type} onChange={(e) => updLine(i, "fabric_type", e.target.value)} data-testid={`po-line-fabric-${i}`} />
                        <Input className="col-span-2" placeholder="Colour" value={it.colour} onChange={(e) => updLine(i, "colour", e.target.value)} />
                        <Select className="col-span-1" value={it.unit} onChange={(e) => updLine(i, "unit", e.target.value)} options={["MTR", "KG"]} />
                      </>
                    ) : (
                      <>
                        <div className="col-span-5 flex gap-1">
                          <Input className="flex-1" list="po-acc-names" value={it.item_name} onChange={(e) => typeAcc(i, e.target.value)} placeholder="Type accessory description" data-testid={`po-line-acc-${i}`} />
                          <button type="button" title="New item" onClick={() => setNewAcc({ category: "Thread", name: "", unit: "Pcs", opening_stock: 0, rate: "", lineIdx: i })} className="px-2 rounded-md border border-slate-300 text-blue-700 text-lg leading-none">+</button>
                        </div>
                        <Select className="col-span-1" value={it.unit} onChange={(e) => updLine(i, "unit", e.target.value)} options={["Pcs", "Kg", "Mtr", "Roll", "Box"]} />
                      </>
                    )}
                    <Input className="col-span-2" type="number" placeholder="Qty" value={it.qty} onChange={(e) => updLine(i, "qty", e.target.value)} data-testid={`po-line-qty-${i}`} />
                    <Input className="col-span-2" type="number" placeholder="Rate" value={it.rate} onChange={(e) => updLine(i, "rate", e.target.value)} data-testid={`po-line-rate-${i}`} />
                    <span className="col-span-1 text-sm text-right tabular">{money((parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0))}</span>
                    <button type="button" onClick={() => rmLine(i)} className="col-span-1 text-red-400 hover:text-red-600 justify-self-end"><Trash2 size={15} /></button>
                  </div>
                ))}
                {!form.items.length && <p className="text-sm text-slate-400 py-2">No items yet — add a fabric or accessory line.</p>}
                <datalist id="po-acc-names">{(accessories || []).map((a) => <option key={a.id} value={a.name}>{a.category}</option>)}</datalist>
              </div>
              <div className="flex justify-end mt-2 text-sm font-semibold text-slate-700">Total: <span className="ml-2 tabular">{money(total)}</span></div>
            </div>

            <Field label="Remarks"><Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="po-save">Save Purchase Order</Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Detail + receive */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={`Purchase Order ${detail?.code || ""}`} wide>
        {detail && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3 text-sm">
              {[["Supplier", detail.supplier_name], ["Order Date", fmtDate(detail.order_date)], ["Expected", fmtDate(detail.expected_receiving_date)], ["Status", detail.status]].map(([l, v]) => (
                <div key={l} className="bg-slate-50 rounded-md p-2"><p className="text-xs text-slate-500">{l}</p><p className="font-medium mt-0.5">{v}</p></div>
              ))}
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase"><tr>
                  <th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Type</th>
                  <th className="text-right px-3 py-2">Ordered</th><th className="text-right px-3 py-2">Received</th>
                  <th className="text-right px-3 py-2">Balance</th><th className="text-left px-3 py-2">Status</th><th></th></tr></thead>
                <tbody>{detail.items.map((it) => (
                  <tr key={it.line_id} className="border-t">
                    <td className="px-3 py-2 font-medium">{it.fabric_type || it.item_name} {it.colour ? `· ${it.colour}` : ""}</td>
                    <td className="px-3 py-2"><StatusBadge status={it.category} /></td>
                    <td className="px-3 py-2 text-right tabular">{num(it.qty)} {it.unit}</td>
                    <td className="px-3 py-2 text-right tabular">{num(it.received_qty)}</td>
                    <td className="px-3 py-2 text-right tabular">{num(it.balance_qty)}</td>
                    <td className="px-3 py-2"><StatusBadge status={it.status} /></td>
                    <td className="px-3 py-2 text-right">{it.balance_qty > 0 && <Button size="sm" onClick={() => openReceive(it)} data-testid={`receive-line-${it.line_id}`}>Receive</Button>}</td>
                  </tr>))}</tbody>
              </table>
            </div>
            {detail.receipts?.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-1">Receipt / Lot History</p>
                <div className="flex flex-wrap gap-2">
                  {detail.receipts.map((r) => <span key={r.id} className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-800 border border-blue-100">{r.lot_number} · {num(r.quantity)} {r.unit} · {fmtDate(r.receiving_date)}</span>)}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => navigate(`/purchase-orders/${detail.id}/print`)}><Printer size={14} /> Print / PDF</Button>
              <Button variant="secondary" onClick={() => sendEmail(detail)}><Mail size={14} /> Email</Button>
              <Button variant="secondary" onClick={() => sendWhatsApp(detail)}><MessageCircle size={14} /> WhatsApp</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Receive line */}
      <Modal open={!!(receiveLine && recForm)} onClose={() => { setReceiveLine(null); setRecForm(null); }} title={`Receive — ${receiveLine?.fabric_type || receiveLine?.item_name || ""}`}>
        {receiveLine && recForm && (
          <form onSubmit={submitReceive} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 bg-slate-50 rounded p-2 text-sm">Balance to receive: <b>{num(receiveLine.balance_qty)} {receiveLine.unit}</b></div>
            <Field label="Received Qty" required><Input type="number" step="any" value={recForm.received_qty} onChange={(e) => setRecForm({ ...recForm, received_qty: e.target.value })} required data-testid="po-rec-qty" /></Field>
            <Field label="Receiving Date"><Input type="date" value={recForm.receiving_date} onChange={(e) => setRecForm({ ...recForm, receiving_date: e.target.value })} /></Field>
            <Field label="Bill Number"><Input value={recForm.bill_number} onChange={(e) => setRecForm({ ...recForm, bill_number: e.target.value })} /></Field>
            <Field label="Bill Date"><Input type="date" value={recForm.bill_date} onChange={(e) => setRecForm({ ...recForm, bill_date: e.target.value })} /></Field>
            {receiveLine.category === "Fabric" && (
              <>
                <Field label="Lot Number"><Input value={recForm.lot_number} onChange={(e) => setRecForm({ ...recForm, lot_number: e.target.value })} placeholder="Auto if blank" data-testid="po-rec-lot" /></Field>
                <Field label="No. of Rolls"><Input type="number" value={recForm.rolls} onChange={(e) => setRecForm({ ...recForm, rolls: e.target.value })} /></Field>
                <Field label="Transporter"><Input value={recForm.transporter} onChange={(e) => setRecForm({ ...recForm, transporter: e.target.value })} /></Field>
                <Field label="Docket Number"><Input value={recForm.docket_number} onChange={(e) => setRecForm({ ...recForm, docket_number: e.target.value })} /></Field>
              </>
            )}
            <Field label="Remarks" className="col-span-2"><Textarea value={recForm.remarks} onChange={(e) => setRecForm({ ...recForm, remarks: e.target.value })} /></Field>
            <div className="col-span-2 flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => { setReceiveLine(null); setRecForm(null); }}>Cancel</Button>
              <Button type="submit" data-testid="po-rec-save">Confirm Receipt</Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Quick create accessory */}
      <Modal open={!!newAcc} onClose={() => setNewAcc(null)} title="Quick Create Accessory Item">
        {newAcc && (
          <form onSubmit={createAcc} className="grid grid-cols-2 gap-4">
            <Field label="Category"><Select value={newAcc.category} onChange={(e) => setNewAcc({ ...newAcc, category: e.target.value })} options={ACC_CATEGORIES} /></Field>
            <Field label="Unit"><Select value={newAcc.unit} onChange={(e) => setNewAcc({ ...newAcc, unit: e.target.value })} options={["Pcs", "Kg", "Mtr", "Roll", "Box"]} /></Field>
            <Field label="Item Name" required className="col-span-2"><Input value={newAcc.name} onChange={(e) => setNewAcc({ ...newAcc, name: e.target.value })} required data-testid="po-newacc-name" /></Field>
            <Field label="Opening Stock"><Input type="number" value={newAcc.opening_stock} onChange={(e) => setNewAcc({ ...newAcc, opening_stock: e.target.value })} /></Field>
            <Field label="Rate"><Input type="number" value={newAcc.rate} onChange={(e) => setNewAcc({ ...newAcc, rate: e.target.value })} /></Field>
            <div className="col-span-2 flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setNewAcc(null)}>Cancel</Button>
              <Button type="submit" data-testid="po-newacc-save">Create Item</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
